import * as _ from 'lodash';
import * as moment from 'moment';

import { AlarmRange, AlarmArea, TagDateValue, Alarm, AlarmLine, AlarmList, AlarmLevel, SeriesDefinition } from '../../models';
import { IHorizontalLineFormat } from '../../wtFramework';
import { IValueTypeHelperService } from '../../services';
import { errorTextColor, errorTextLightColor, infoTextColor, infoTextLightColor, warningTextColor, warningTextLightColor } from '../../common/constants';

export interface IAlarmHelper {
    alarmLevelColor(level: AlarmLevel): string;
    alarmLineLevelColor(level: AlarmLevel): string;
    filterAlarmOnDataValueType(alarmList: AlarmList, data: TagDateValue[]): Alarm[];
    getAlarmAreas(values: TagDateValue[], endDate: string, availableAlarms: Alarm[]): AlarmArea[];
    getAlarmAreasOnRanges(range: AlarmRange[]): AlarmArea[];
    getHorizontalLines(series: SeriesDefinition<TagDateValue>[]): IHorizontalLineFormat[];
    getMinMaxLines(alarms: Alarm[], alarm?: Alarm, displayAll?: boolean): AlarmLine[];
    getRange(current: TagDateValue, currentAlarm: number): AlarmRange;
    getRanges(values: TagDateValue[], endDate: string, allowedLevels: number[]): AlarmRange[];
    toAlarmsArray(alarms: AlarmList): Alarm[];
}

export class AlarmHelper implements IAlarmHelper {
    static helperId = 'alarmHelper';

    static $inject = ['valueTypeHelperService'];

    constructor(private _valueTypeHelperService: IValueTypeHelperService) {}

    getRanges(values: TagDateValue[], endDate: string, allowedLevels: number[]): AlarmRange[] {
        return values
            .reduce((acc: AlarmRange[], current, i, arr) => {
                let prev = acc.length > 0 ? acc[acc.length - 1] : null;
                let currentAlarm: number = current.alarm;

                if (allowedLevels.indexOf(currentAlarm) < 0) {
                    currentAlarm = 0;
                }

                // create intial range
                if (!prev && currentAlarm > 0) {
                    prev = this.getRange(current, currentAlarm);
                    acc.push(prev);
                }

                if (prev) {
                    // progressively extend the range
                    prev.end = current.timeStamp;

                    // jump to next range
                    if (prev.level !== currentAlarm - 1) {
                        if (currentAlarm > 0) {
                            acc.push(this.getRange(current, currentAlarm));
                        } else {
                            acc.push(null);
                        }
                        // special case for end
                    } else if (i === arr.length - 1) {
                        prev.end = endDate;
                    }
                }
                return acc;
            }, [])
            .filter((v) => v !== null);
    }

    getRange(current: TagDateValue, currentAlarm: number): AlarmRange {
        return { start: current.timeStamp, end: current.timeStamp, level: currentAlarm - 1 };
    }

    getAlarmAreasOnRanges(ranges: AlarmRange[]): AlarmArea[] {
        return ranges
            .sort((a, b) => (a.level > b.level ? 1 : -1))
            .map((r) => {
                return {
                    beginDate: moment(r.start).toDate(),
                    endDate: moment(r.end).toDate(),
                    color: this.alarmLevelColor(r.level),
                    opacity: 0.3
                };
            });
    }

    getAlarmAreas(values: TagDateValue[], endDate: string, availableAlarms: Alarm[]): AlarmArea[] {
        const ranges = this.getRanges(
            values,
            endDate,
            availableAlarms.map((a) => a.level + 1)
        );
        const areas: AlarmArea[] = this.getAlarmAreasOnRanges(ranges);
        return areas;
    }

    getMinMaxLines(alarms: Alarm[], alarm?: Alarm, displayAll?: boolean): AlarmLine[] {
        if (!!displayAll && !alarm) {
            return this.loadMinMax(alarms, alarm);
        } else {
            return alarm ? this.loadMinMax(alarms, alarm) : [];
        }
    }

    private loadMinMax(alarms: Alarm[], alarm?: Alarm): AlarmLine[] {
        let lines = [];

        if (alarms) {
            if (alarm) {
                alarms.filter((a) => a.value === alarm.value).forEach((c) => (lines = lines.concat(this.buildMinMaxLines(c))));
            } else {
                alarms.forEach((c) => (lines = lines.concat(this.buildMinMaxLines(c))));
            }
        }

        return lines;
    }

    private buildMinMaxLines(currentAlarm: Alarm): AlarmLine[] {
        const color = this.alarmLineLevelColor(currentAlarm.level);
        const lines: AlarmLine[] = [];
        const multiplier = this._valueTypeHelperService.isValueTypePercent(currentAlarm.value) ? 100 : 1;

        if (currentAlarm.low !== undefined && !currentAlarm.isManual) {
            const alarmLine: AlarmLine = { value: currentAlarm.low * multiplier, color: color };
            lines.push(alarmLine);
        }

        if (currentAlarm.high !== undefined && !currentAlarm.isManual) {
            const alarmLine: AlarmLine = { value: currentAlarm.high * multiplier, color: color };
            lines.push(alarmLine);
        }

        return lines;
    }

    filterAlarmOnDataValueType(alarmList: AlarmList, data: TagDateValue[]): Alarm[] {
        let availableAlarms: Alarm[] = [];
        const identifierGuids = _.uniq(data.filter((d) => d.guid).map((d) => d.guid));
        identifierGuids.forEach((guid) => {
            if (alarmList[guid]) {
                const dataValueTypes: string[] = _.uniq(data.map((a) => a.valueType)).filter((a) => !!a);
                const alarms = alarmList[guid].filter((a) => dataValueTypes.indexOf(a.value) >= 0);
                availableAlarms = availableAlarms.concat(alarms);
            }
        });
        return availableAlarms;
    }

    toAlarmsArray(alarms: AlarmList): Alarm[] {
        return _.flatten(Object.values(alarms));
    }

    alarmLevelColor(level: AlarmLevel): string {
        return level === AlarmLevel.Critical ? errorTextLightColor : level === AlarmLevel.Warning ? warningTextLightColor : infoTextLightColor;
    }

    alarmLineLevelColor(level: AlarmLevel): string {
        let levelColor: string;
        switch (level) {
            case AlarmLevel.Critical:
                levelColor = errorTextColor;
                break;
            case AlarmLevel.Warning:
                levelColor = warningTextColor;
                break;
            default:
                levelColor = infoTextColor;
                break;
        }
        return levelColor;
    }

    getHorizontalLines(series: SeriesDefinition<TagDateValue>[]): IHorizontalLineFormat[] {
        return [].concat(
            ...series.filter((s) => s.alarms && s.alarms.length && !s.isStacked).map((s) => [].concat(...s.alarms.map((a) => this.mapHorizontalLines(a, s))))
        );
    }

    private mapHorizontalLines(a: Alarm, s: SeriesDefinition<TagDateValue>) {
        const res: IHorizontalLineFormat[] = [];
        const isPercent = this._valueTypeHelperService.isValueTypePercent(a.value);
        let tmp = this.getLineFormat(a.low, isPercent, a, s);
        if (tmp) {
            res.push(tmp);
        }
        tmp = this.getLineFormat(a.high, isPercent, a, s);
        if (tmp) {
            res.push(tmp);
        }
        return res;
    }

    private getLineFormat(origValue: number, isPercent: boolean, a: Alarm, s: SeriesDefinition<TagDateValue>) {
        if (origValue !== undefined && origValue !== null) {
            const value = isPercent ? origValue * 100 : origValue;
            const tmp: IHorizontalLineFormat = {
                value: value,
                color: this.alarmLevelColor(a.level),
                isHidden: !s.isVisible || s.serieState === 1,
                seriesId: s.identifier
            };
            if (s.hasOwnAxis) {
                tmp.axis = s.identifier;
            }
            return tmp;
        }
        return null;
    }
}
